import React, { useState } from 'react';
import { MapPin, Mail, Send, Paperclip, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from '../hooks/useTranslation';
import { mockData } from '../data/mockData';
import EditableText from './EditableText';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    attachFile: false
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      // Max 10MB per file
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: `${file.name} is larger than 10MB`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) return;
    
    // Validate required fields
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast({
        title: t('contact.validationError') || "Validation Error",
        description: t('contact.fillRequiredFields') || "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: t('contact.validationError') || "Validation Error",
        description: t('contact.invalidEmail') || "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        throw new Error('Backend URL is not configured. Please set REACT_APP_BACKEND_URL environment variable.');
      }
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim().toLowerCase());
      formDataToSend.append('company', formData.company?.trim() || '');
      formDataToSend.append('message', formData.message.trim());
      formDataToSend.append('attachFile', selectedFiles.length > 0 ? 'true' : 'false');
      
      // Append files
      selectedFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        body: formDataToSend, // No Content-Type header - browser sets it automatically with boundary
      });

      if (response.ok) {
        const result = await response.json();
        
        toast({
          title: t('contact.messageSent') || "Message Sent Successfully!",
          description: t('contact.responseTime') || "We'll get back to you within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          attachFile: false
        });
        setSelectedFiles([]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: t('contact.errorTitle') || "Error",
        description: t('contact.errorMessage') || "Failed to send message. Please try again or contact us directly at office@towertech-xm.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#0A192F] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16">
          <EditableText
            section="contact"
            field="title"
            as="h2"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            {t('contact.title')}
          </EditableText>
          <EditableText
            section="contact"
            field="description"
            as="p"
            className="text-lg sm:text-xl text-slate-300"
          >
            {t('contact.description')}
          </EditableText>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <EditableText
              section="contact"
              field="ourOfficeTitle"
              as="h3"
              className="text-2xl font-bold mb-8 text-white"
            >
              {t('contact.ourOffice')}
            </EditableText>
            
            {mockData.offices.map((office, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500 p-3 rounded-full flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-blue-400">{office.city}</h4>
                    <p className="text-slate-300 mb-2">{office.address}</p>
                    <a 
                      href={`mailto:${office.email}`}
                      className="flex items-center text-white hover:text-blue-400 transition-colors duration-300"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {office.email}
                    </a>
                    <p className="text-slate-400 text-sm mt-2">
                      {t('contact.foundedLabel')}: 2019
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-xl" data-testid="contact-form">
            <EditableText
              section="contact"
              field="formTitle"
              as="h3"
              className="text-2xl font-bold mb-4 text-slate-900"
            >
              {t('contact.howHelp')}
            </EditableText>
            <EditableText
              section="contact"
              field="formDescription"
              as="p"
              className="text-slate-600 mb-6 text-sm sm:text-base"
            >
              {t('contact.requirementsDetail')}
            </EditableText>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder={`${t('contact.fullName')} *`}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:border-blue-500 focus:ring-blue-500"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder={`${t('contact.email')} *`}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:border-blue-500 focus:ring-blue-500"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <Input
                type="text"
                name="company"
                placeholder={t('contact.company')}
                value={formData.company}
                onChange={handleChange}
                className="bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500"
                data-testid="contact-company-input"
              />

              <Textarea
                name="message"
                placeholder={t('contact.tellUs')}
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                className="bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 resize-none text-sm focus:border-blue-500 focus:ring-blue-500"
                data-testid="contact-message-input"
              />

              {/* File Upload Section */}
              <div className="space-y-3">
                <label className="flex items-center justify-center w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-slate-100 transition-all">
                  <Paperclip className="w-5 h-5 mr-2 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {selectedFiles.length > 0 
                      ? `${selectedFiles.length} ${t('contact.filesSelected')}` 
                      : t('contact.attachFiles')}
                  </span>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    className="hidden"
                    data-testid="contact-file-input"
                  />
                </label>
                
                {/* Display selected files */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border border-slate-200">
                        <span className="text-sm text-slate-600 truncate flex-1">
                          {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500">
                      Max 10MB per file. Supported: Images, PDF, Word, Excel
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="privacy"
                  required
                  className="w-4 h-4 mt-1 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  data-testid="contact-privacy-checkbox"
                />
                <label htmlFor="privacy" className="text-slate-600 text-sm">
                  {t('contact.privacyAccept')} <a href="#" className="text-blue-600 hover:text-blue-800 underline">{t('contact.privacyPolicies')}</a>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 sm:py-4 text-sm sm:text-lg font-semibold transition-all duration-300 transform hover:scale-[1.02] disabled:transform-none disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                <Send className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 ${isSubmitting ? 'animate-spin' : ''}`} />
                {isSubmitting ? (t('contact.sending') || 'Sending...') : t('contact.sendMessage')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;